import { create } from "zustand";
import {
  INotificationBaseRelatedToShow,
  NotificationType,
  Roles,
} from "@evennt-tech/api-v2-core";
import { apiV2 } from "api/firebase";
import { devtools } from "zustand/middleware";
import { IdleManager } from "tools/idle";
import {
  IAddColorBodyReq,
  IDeleteColorQueryReq,
  IGetDefaultColorsRes,
  IGetImportableShowsParamsReq,
  IGetOneBrandingRes,
  IImportableShowRes,
  IImportBrandingReq,
  IUpdateBackgroundBodyReq,
  IUpdateBannerBodyReq,
  IUpdatePrimaryColorBodyReq,
  IUpdateThemeBodyReq,
} from "@evennt-tech/api-v2-external";
import { registerStore } from "store/utils/reset-store.utils";
import { getRegisteredStore } from "store/store-registry";

export type Branding = IGetOneBrandingRes | null;

type Brandings = {
  [showId: string]: Branding;
};

type State = {
  brandings: Brandings;
  defaultColors: IGetDefaultColorsRes[];
  isLoading: boolean;
};

type Action = {
  fetch: (showId: string, params?: { force?: boolean }) => Promise<void>;
  fetchMany: (showIds: string[], force?: boolean) => Promise<void>;
  autoCatch: (showId: string) => Promise<void>;

  updateBanner: (params: IUpdateBannerBodyReq) => Promise<void>;
  updateBackground: (params: IUpdateBackgroundBodyReq) => Promise<void>;
  updatePrimaryColor: (params: IUpdatePrimaryColorBodyReq) => Promise<void>;
  updateTheme: (params: IUpdateThemeBodyReq) => Promise<void>;

  getImportableShows: (
    params: IGetImportableShowsParamsReq
  ) => Promise<IImportableShowRes[]>;
  importFromShow: (params: IImportBrandingReq) => Promise<void>;

  getBranding: (showId: string) => Branding;

  updateState: (showId: string) => Promise<void>;

  addColor: (params: IAddColorBodyReq) => Promise<void>;
  deleteColor: (params: IDeleteColorQueryReq) => Promise<void>;

  setBranding: (showId: string, branding: Branding) => void;
};

const BRANDING_ROLES: Roles[] = [
  Roles.ORGANISER,
  Roles.EXHIBITOR,
  Roles.SUPPLIER,
];

const canAccessBranding = (): boolean => {
  try {
    return BRANDING_ROLES.includes(getRegisteredStore().getState().auth?.role);
  } catch {
    return false;
  }
};

const useBranding = create(
  devtools<State & Action>((set, get) => ({
    brandings: {},
    defaultColors: [],
    isLoading: false,

    getBranding: (showId) => get().brandings[showId],

    setBranding: (showId, branding) =>
      set((old) => ({
        ...old.brandings,
        [showId]: branding,
      })),

    fetchMany: async (showIds, force) => {
      if (!canAccessBranding()) {
        return;
      }

      const loadedBrandings = get().brandings;

      let actualShowIds = [...showIds];

      if (!get().defaultColors.length) {
        try {
          const defaultColors = await apiV2.branding.getDefaultColors();
          set(() => ({ defaultColors }));
        } catch (e) {
          console.error("Couldn't load default colors: ", e);
        }
      }

      if (!force) {
        actualShowIds = showIds.filter((id) => !loadedBrandings[id]);
        if (!actualShowIds.length) {
          return;
        }
      }

      const brandings = await apiV2.branding.getManyBrandings({
        showIds: actualShowIds,
      });

      const mappedBrandings = brandings.reduce<Brandings>((acc, cur) => {
        const { showId } = cur;
        acc[showId] = cur;
        return acc;
      }, {});

      set((old) => ({
        brandings: {
          ...old.brandings,
          ...mappedBrandings,
        },
      }));
    },

    addColor: async (params) => {
      await apiV2.branding.addColor(params);
    },

    deleteColor: async (params) => {
      await apiV2.branding.deleteColor(params);
    },

    updateState: async (showId) => {
      if (!canAccessBranding()) {
        return;
      }

      set(() => ({ isLoading: true }));

      try {
        const data = await apiV2.branding.getOne({ showId });
        set((old) => ({
          brandings: {
            ...old.brandings,
            [showId]: data,
          },
        }));
      } catch (e) {
        console.error("ERROR DURING LOADING BRANDING: ", e);
      } finally {
        set(() => ({ isLoading: false, showId }));
      }
    },

    updateBackground: (params) => apiV2.branding.updateBackground(params),
    updateBanner: (params) => apiV2.branding.updateBanner(params),
    updateTheme: (params) => apiV2.branding.updateTheme(params),
    updatePrimaryColor: (params) => apiV2.branding.updatePrimaryColor(params),

    getImportableShows: (params) => apiV2.branding.getImportableShows(params),
    importFromShow: (params) => apiV2.branding.importFromShow(params),

    autoCatch: async (showId: string) => {
      if (!showId) {
        return;
      }
      const { updateState } = get();
      IdleManager.getInstance().addSubscriber(
        `branding-${showId}`,
        async () => {
          await updateState(showId);
        }
      );

      apiV2.notification.subscribe<INotificationBaseRelatedToShow>({
        key: NotificationType.UPDATED_BRANDING,
        showId,
        callback: async ({ showId }) => {
          await updateState(showId);
        },
      });
    },

    fetch: async (showId: string, params) => {
      if (!canAccessBranding()) {
        return;
      }

      if (!get().defaultColors.length) {
        try {
          const defaultColors = await apiV2.branding.getDefaultColors();
          set(() => ({ defaultColors }));
        } catch (e) {
          console.error("Couldn't load default colors: ", e);
        }
      }

      if (!showId) {
        return;
      }
      const { brandings, updateState } = get();

      if (!params?.force && !!brandings[showId]) {
        return;
      }

      await updateState(showId);
    },
  }))
);

registerStore(useBranding);

export default useBranding;
